import { Button, Form, InputField, Search } from "@myloc/myloc-gui";
import { useHistory } from "@myloc/myloc-utils";
import PropType from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory as useRouterHistory } from "react-router-dom";
import { useTranslate } from "../../../../language/i18n";
import messageService from "../../../../services/message/messageService";
import pages from "../../../../utils/pages";
import { useMobile } from "../../../../utils/viewport";
import Page from "../../../shared/Page/Page";
import Paginate from "../../../shared/Pagination/Paginate";
import RadioButtonGroup from "../../../shared/RadioButton/RadioButtonGroup";
import MessageDetail from "../MessageDetail/MessageDetail";
import MessageRow from "../MessageRow/MessageRow";
import { MESSAGE_TYPE } from "../MessageTypes";
import styles from "./Message.module.scss";

const DEFAULT_FILTER = "ALL";
const SELECTED_PARAM = "selected";

const MESSAGE_NAME_MAP = {
  [MESSAGE_TYPE.INBOX]: pages.MESSAGES_INBOX.NAME,
  [MESSAGE_TYPE.SENT]: pages.MESSAGES_SENT.NAME,
  [MESSAGE_TYPE.ARCHIVED]: pages.MESSAGES_ARCHIVED.NAME,
  [MESSAGE_TYPE.ALL]: pages.MESSAGES_ALL.NAME,
};

const MESSAGE_BASE_FILTERS = {
  [MESSAGE_TYPE.INBOX]: { status: "20:=", isSender: false },
  [MESSAGE_TYPE.SENT]: { isSender: true },
  [MESSAGE_TYPE.ARCHIVED]: { status: "90:=" },
  [MESSAGE_TYPE.ALL]: { status: "20:>=;90:<=" },
};

const IS_READ_FILTER_TYPES = new Set([MESSAGE_TYPE.INBOX, MESSAGE_TYPE.ALL]);

const Message = ({ setBreadcrumbs, messageType }) => {
  const translate = useTranslate();
  const showFilters = messageType === MESSAGE_TYPE.ALL || messageType === MESSAGE_TYPE.INBOX;
  const history = useHistory();
  const routerHistory = useRouterHistory();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const isMobile = useMobile();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [searchValue, setSearchValue] = useState(params.get("q") || "");
  const [searchValueText, setSearchValueText] = useState();
  const [loading, setLoading] = useState(false);

  const selectedMessageId = new URLSearchParams(location.search).get(SELECTED_PARAM) || "";

  const setSelectedMessageId = useCallback(
    messageId => {
      const currentParams = new URLSearchParams(window.location.search);
      if (messageId) {
        currentParams.set(SELECTED_PARAM, messageId);
      } else {
        currentParams.delete(SELECTED_PARAM);
      }

      const newSearch = currentParams.toString();
      routerHistory.push(`${window.location.pathname}${newSearch ? "?" + newSearch : ""}`);
    },
    [routerHistory],
  );

  const [readValue, setReadValue] = useState(showFilters ? params.get("filter") || DEFAULT_FILTER : null);
  const firstParamCheck = useRef(true);

  const filterStates = [
    { id: "ALL", value: translate("ALL") },
    { id: "UNREAD", value: translate("UNREAD") },
  ];

  const handleOnClickMessage = message => {
    setSelectedMessageId(message?.id);
  };

  const filterSearch = useCallback(() => {
    setSelectedMessageId("");

    setSearchValueText(searchValue);
  }, [searchValue, setSelectedMessageId]);

  const getMessages = useCallback(
    filter => {
      const isRead = IS_READ_FILTER_TYPES.has(messageType) ? (readValue === "UNREAD" ? false : null) : undefined;
      return messageService.getMessages({
        searchValue: searchValueText,
        sort: "SentDate:desc",
        ...(isRead !== undefined && { isRead }),
        ...MESSAGE_BASE_FILTERS[messageType],
        ...filter,
      });
    },
    [messageType, searchValueText, readValue],
  );

  const getMessageName = useCallback(() => MESSAGE_NAME_MAP[messageType], [messageType]);

  const clear = useCallback(() => {
    setSelectedMessageId("");
    setSearchValue("");
    setSearchValueText("");
  }, [setSelectedMessageId]);

  const handleIsReadChange = async selected => {
    setReadValue(selected.id);
    clear();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    let paramsChanged = false;

    if (searchValue === "") {
      if (params.get("q")) {
        params.delete("q");
        paramsChanged = true;
      }
    } else if (searchValue !== params.get("q")) {
      params.set("q", searchValue);
      paramsChanged = true;
    }

    if (showFilters) {
      if (params.get("filter") !== readValue) {
        params.set("filter", readValue);
        paramsChanged = true;
      }
    }

    if (paramsChanged) history.replace(`${window.location.pathname}${window.location.search}`, params);
  }, [searchValue, history, readValue, showFilters]);

  useEffect(() => {
    return () => {
      setSearchValue("");
      setSearchValueText("");
    };
  }, [messageType]);

  useEffect(() => {
    if (firstParamCheck.current && params.get("q") != null) {
      firstParamCheck.current = false;
      filterSearch();
    }
  }, [filterSearch, params]);

  useEffect(() => {
    setBreadcrumbs([{ text: `${translate(pages.MESSAGES.NAME)} - ${translate(getMessageName())}` }]);
  }, [setBreadcrumbs, translate, getMessageName]);

  return (
    <Page customCssClass={styles.page} title={translate(getMessageName())}>
      <div>
        <div className={selectedMessageId ? styles.listHidden : styles.list}>
          <Form onSubmit={filterSearch}>
            <div className={styles.generalSearch}>
              <InputField
                label={translate("SEARCH_FOR_MESSAGE")}
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                onClear={() => setSearchValue("")}
              />
              <Button type="submit" customCssClass={styles.searchButton}>
                {isMobile ? <Search /> : translate("SEARCH")}
              </Button>
            </div>
          </Form>
          {showFilters && (
            <RadioButtonGroup
              values={filterStates}
              onChange={handleIsReadChange}
              selected={readValue}
              customCssClass={styles.filter}
            />
          )}
          <Paginate onChange={setMessages} provider={getMessages} isLoading={loading} setIsLoading={setLoading}>
            {!!messages.length &&
              messages.map(message => (
                <MessageRow
                  key={message.id}
                  message={message}
                  handleOnClickMessage={handleOnClickMessage}
                  activeMessage={selectedMessageId === message.id ?? false}
                  setSelectedMessageId={setSelectedMessageId}
                  setMessages={setMessages}
                  messageType={messageType}
                />
              ))}
          </Paginate>
        </div>
        <div>
          {selectedMessageId && (
            <MessageDetail messageId={selectedMessageId} setMessages={setMessages} messageType={messageType} />
          )}
        </div>
      </div>
    </Page>
  );
};

Message.propTypes = {
  setBreadcrumbs: PropType.func.isRequired,
  messageType: PropType.string.isRequired,
};

export default Message;
